import Map, {MapRef, Marker} from 'react-map-gl';
import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {gpx} from '@tmcw/togeojson';
import mapboxgl, {PaddingOptions} from 'mapbox-gl';
import {StateContext} from '../ContextProvider';

const cartographyMapping: { [key: string]: string } = {
    dark: 'mapbox://styles/jordan57680/cm94798hz00ac01r2f8seab29',
    light: 'mapbox://styles/jordan57680/cm99pk9y1002901s384op85v8',
    outdoor: 'mapbox://styles/jordan57680/cm943c9td009501sk8b66a10m',
    cali: 'mapbox://styles/jordan57680/cm943kb0x00a501r23yxl8xuv',
    full_black: 'mapbox://styles/jordan57680/cm94798hz00ac01r2f8seab29?full-black',
    full_white: 'mapbox://styles/jordan57680/cm94798hz00ac01r2f8seab29?full-white',
};

const MapDraw = ({fitBoundsPadding, lineStroke}: {fitBoundsPadding: PaddingOptions, lineStroke: number}) => {
    const [geojsonData, setGeojsonData] = useState<any>(null);
    const {raceData, xmlParsed, markers} = useContext(StateContext);
    const mapContainer = useRef<MapRef>(null);

    const fitMapToBounds = useCallback((geojson: any) => {
        if (!mapContainer.current || !geojson?.features) return;

        const map = mapContainer.current.getMap();
        const coordinates = geojson.features.flatMap((f: any) => f.geometry.coordinates);

        if (coordinates.length > 0) {
            const bounds = coordinates.reduce(
                (bounds: any, coord: any) => bounds.extend(coord),
                new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
            );

            map.fitBounds(bounds, { padding: fitBoundsPadding, duration: 1000 });
        }
    }, [fitBoundsPadding]);

    const handleMapLoad = useCallback((geojson: any) => {
        if (geojson) {
            fitMapToBounds(geojson);
        }
    }, [fitMapToBounds]);

    const _loadFile = useCallback(async () => {
        let geojson;
        if (!xmlParsed) {
            return;
        }
        geojson = gpx(xmlParsed);

        setGeojsonData(geojson);
        handleMapLoad(geojson);
    }, [xmlParsed, handleMapLoad]);

    useEffect(() => {
        if (xmlParsed) {
            _loadFile();
        }
    }, [xmlParsed, _loadFile]);

    useEffect(() => {
        const checkMapReady = () => {
            if (mapContainer.current && mapContainer.current.getMap()) {
                const map = mapContainer.current.getMap();
                map.setStyle(cartographyMapping[raceData.cartography]);
            } else {
                // Réessaie après un court délai
                setTimeout(checkMapReady, 100);
            }
        };

        checkMapReady();
    }, [mapContainer]);

    useEffect(() => {
        const map = mapContainer.current?.getMap();

        if (!map) return;

        // Attends la fin du chargement du nouveau style
        const handleStyleLoad = () => {
            // ici ré-ajoute tes sources et layers après chargement du style
            if (geojsonData && geojsonData.features) {
                if (!map.getSource('gpx')) {
                    map.addSource('gpx', {
                        type: 'geojson',
                        data: geojsonData,
                    });
                }

                if (['full_black', 'full_white'].includes(raceData.cartography)) {
                    const backgroundColor = raceData.cartography === 'full_black' ? '#000' : '#ffffff';
                    if (!map.getLayer('background-layer')) {
                        map.addLayer({
                            id: 'background-layer',
                            type: 'background',
                            paint: { 'background-color': backgroundColor },
                        });
                    }
                }

                if (!map.getLayer('gpx-line')) {
                    map.addLayer({
                        id: 'gpx-line',
                        type: 'line',
                        source: 'gpx',
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round',
                        },
                        paint: {
                            'line-color': raceData.color,
                            'line-width': lineStroke,
                        },
                    });
                }

                fitMapToBounds(geojsonData);
            }
        };

        map.setStyle(null);
        map.setStyle(cartographyMapping[raceData.cartography]);
        map.once('style.load', handleStyleLoad);

    }, [raceData.cartography, geojsonData, mapContainer, lineStroke, raceData.color, fitMapToBounds, fitBoundsPadding]);

    return (
        <Map
            mapboxAccessToken={'pk.eyJ1Ijoiam9yZGFuNTc2ODAiLCJhIjoiY20zdWRxcWRpMGZ5ZzJqcjBzZDFwZWhpbiJ9.IlzgPXyuSSH0RXJWM30eqg'}
            interactive={false}
            style={{width: '100%', height: '100%'}}
            attributionControl={false}
            onLoad={() => handleMapLoad(geojsonData)}
            preserveDrawingBuffer={true}
            ref={mapContainer}
            initialViewState={{
                longitude: 6.166667,
                latitude: 49.133333,
                zoom: 12
            }}
        >
            {markers.map((marker) => (
                <Marker key={marker.id} longitude={marker.lng} latitude={marker.lat} style={{color: 'white', fontSize: '23px'}} draggable={true}>
                    {marker.text}
                </Marker>
            ))}
        </Map>
    )
};

export default MapDraw;
